import React, { useEffect, useState } from 'react';
import { Box, Stack, Paper, Divider, Typography, Alert } from '@mui/material';
import SectionTitle from '../components/SectionTitle';
import FilterableList from '../components/FilterableList';
import PostalResultRow from '../components/PostalResultRow';
import { getBreakdown } from '../services/api';
import { t, lang } from '../i18n';
import { pushEvent } from '../utils/analytics';

// Display a possibly-empty selection ("" is a real level, e.g. Tokyo wards).
const shown = (v) => (v === '' ? t('(none)', '（なし）') : v);

// Resolve a selected kanji value to its display label (kanji for ja, romaji for
// en) using the option list it came from. Falls back to the kanji value.
const dispLabel = (options, value) => {
  if (value == null) return '';
  if (lang === 'ja') return value;
  const opt = (options || []).find((o) => o.value === value);
  return (opt && opt.label) || value;
};

const BrowsePage = () => {
  const [prefOptions, setPrefOptions] = useState(null);
  const [cityOptions, setCityOptions] = useState(null);
  const [wardOptions, setWardOptions] = useState(null);
  const [townOptions, setTownOptions] = useState(null);
  const [results, setResults] = useState(null);

  // null = not selected; "" = selected-empty (a real level).
  const [pref, setPref] = useState(null);
  const [city, setCity] = useState(null);
  const [ward, setWard] = useState(null);
  const [town, setTown] = useState(null);

  const [loading, setLoading] = useState({});
  const setBusy = (key, val) => setLoading((s) => ({ ...s, [key]: val }));

  // Load prefecture list once (GET /breakdown with no params).
  useEffect(() => {
    let active = true;
    setBusy('pref', true);
    getBreakdown({})
      .then((r) => active && setPrefOptions(r.options || []))
      .catch(() => active && setPrefOptions([]))
      .finally(() => active && setBusy('pref', false));
    return () => {
      active = false;
    };
  }, []);

  const selectPref = async (value) => {
    setPref(value);
    setCity(null); setWard(null); setTown(null);
    setCityOptions(null); setWardOptions(null); setTownOptions(null); setResults(null);
    pushEvent('browse_selection', { level: 'pref', value });
    setBusy('city', true);
    try {
      const r = await getBreakdown({ pref: value });
      setCityOptions(r.options || []);
    } catch {
      setCityOptions([]);
    } finally {
      setBusy('city', false);
    }
  };

  const selectCity = async (value) => {
    setCity(value);
    setWard(null); setTown(null);
    setWardOptions(null); setTownOptions(null); setResults(null);
    pushEvent('browse_selection', { level: 'city', value });
    setBusy('ward', true);
    try {
      const r = await getBreakdown({ pref, city: value });
      setWardOptions(r.options || []);
    } catch {
      setWardOptions([]);
    } finally {
      setBusy('ward', false);
    }
  };

  const selectWard = async (value) => {
    setWard(value);
    setTown(null);
    setTownOptions(null); setResults(null);
    pushEvent('browse_selection', { level: 'ward', value });
    setBusy('town', true);
    try {
      const r = await getBreakdown({ pref, city, ward: value });
      setTownOptions(r.options || []);
    } catch {
      setTownOptions([]);
    } finally {
      setBusy('town', false);
    }
  };

  const selectTown = async (value) => {
    setTown(value);
    setResults(null);
    pushEvent('browse_selection', { level: 'town', value });
    setBusy('results', true);
    try {
      const r = await getBreakdown({ pref, city, ward, town: value });
      const res = r.results || [];
      setResults(res);
      pushEvent('result_count', { page: 'browse', count: res.length });
    } catch {
      setResults([]);
    } finally {
      setBusy('results', false);
    }
  };

  return (
    <Box>
      <SectionTitle emoji="🗂">{t('Browse by Area', '地域から探す')}</SectionTitle>

      <Stack spacing={2}>
        <FilterableList
          title={t('Prefecture', '都道府県')}
          options={prefOptions || []}
          selected={pref}
          onSelect={selectPref}
          loading={!!loading.pref}
        />

        {pref !== null && (
          <FilterableList
            title={t('City', '市区町村')}
            subtitle={`(${dispLabel(prefOptions, pref)})`}
            options={cityOptions || []}
            selected={city}
            onSelect={selectCity}
            emptyLabel={t('(skip — no city/district)', '（市・郡なし）')}
            loading={!!loading.city}
          />
        )}

        {city !== null && (
          <FilterableList
            title={t('Ward', '区')}
            subtitle={`(${shown(dispLabel(cityOptions, city))}) · ${t('optional', '任意')}`}
            options={wardOptions || []}
            selected={ward}
            onSelect={selectWard}
            emptyLabel={t('(skip — no ward)', '（区・町・村なし）')}
            loading={!!loading.ward}
          />
        )}

        {ward !== null && (
          <FilterableList
            title={t('Town', '町域')}
            subtitle={`(${shown(dispLabel(cityOptions, city))} ${shown(dispLabel(wardOptions, ward))})`}
            options={townOptions || []}
            selected={town}
            onSelect={selectTown}
            loading={!!loading.town}
          />
        )}
      </Stack>

      {results !== null && (
        <Box sx={{ mt: 3 }}>
          <SectionTitle>{t('Results', '結果')}</SectionTitle>
          {results.length === 0 ? (
            <Alert severity="info">{t('No postal codes found.', '郵便番号が見つかりませんでした。')}</Alert>
          ) : (
            <Paper
              elevation={0}
              sx={{ bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
            >
              {results.map((entry, i) => (
                <React.Fragment key={`${entry.postal_code}-${i}`}>
                  {i > 0 && <Divider />}
                  <PostalResultRow entry={entry} />
                </React.Fragment>
              ))}
            </Paper>
          )}
        </Box>
      )}

      {loading.results && (
        <Typography sx={{ mt: 2 }} color="text.secondary">
          {t('Loading…', '読み込み中…')}
        </Typography>
      )}
    </Box>
  );
};

export default BrowsePage;
